
import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { createNote, getAllCategories } from '@/lib/supabase';
import { Session } from '@supabase/supabase-js';
import ProductImageManager from '@/components/ProductImageManager';
import RichTextEditor from '@/components/RichTextEditor';
import LocationInput from '@/components/LocationInput';
import type { NoteLocation, PromoItem } from '@/types/location';
import CategorySelector from '@/components/CategorySelector';
import PromoDetailsPanel from '@/components/PromoDetailsPanel';
import { useSession } from '@/context/SessionContext';
import { cn } from '@/lib/utils';

const DRAFT_STORAGE_KEY = 'unsaved-note-draft';

// Copiado de ProductForm: trunca el string antes de la primera coma
function truncateBeforeComma(str: string) {
  if (!str) return '';
  const idx = str.indexOf(',');
  return idx === -1 ? str : str.slice(0, idx);
}

// Helper para extraer texto plano del HTML y validar si hay contenido real
function getPlainTextFromHtml(html: string): string {
  if (!html) return '';
  // Remover etiquetas HTML más completas
  let plain = html;
  // Remover todas las etiquetas HTML
  plain = plain.replace(/<[^>]*>/g, '');
  // Decodificar entidades HTML comunes
  plain = plain.replace(/&nbsp;/g, ' ');
  plain = plain.replace(/&amp;/g, '&');
  plain = plain.replace(/&lt;/g, '<');
  plain = plain.replace(/&gt;/g, '>');
  plain = plain.replace(/&quot;/g, '"');
  plain = plain.replace(/&#39;/g, "'");
  // Remover espacios en blanco y saltos de línea
  plain = plain.trim();
  return plain;
}

interface NoteFormProps {
  session: Session | null;
  profile?: any;
  onSuccess?: (noteId?: string) => void;
  hideCard?: boolean;
  hideButton?: boolean;
  proposedContentType?: 'news' | 'promo';
}

export interface NoteFormHandle {
  submit: () => Promise<void>;
  isLoading: boolean;
}

const NoteForm = forwardRef<NoteFormHandle, NoteFormProps>(({ session, profile: propProfile, onSuccess, hideCard = false, hideButton = false, proposedContentType = 'news' }, ref) => {
  const { profile: contextProfile, relevantLocation } = useSession();
  const profile = propProfile || contextProfile;

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [location, setLocation] = useState<NoteLocation | null>(null);
  const [showLocationEditor, setShowLocationEditor] = useState(false);
  const [locationEditorAutoFocus, setLocationEditorAutoFocus] = useState(false);
  const [editingLocation, setEditingLocation] = useState<NoteLocation | null>(null);
  const [images, setImages] = useState<string[]>([]);
  const [mainImage, setMainImage] = useState<string | null>(null);
  const [imagesToDelete, setImagesToDelete] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const defaultLocationSetRef = useRef(false);

  // DEALS/PROMOS fields
  const [storeName, setStoreName] = useState('');
  const [vendorLocation, setVendorLocation] = useState<NoteLocation | null>(null);
  const [promoItems, setPromoItems] = useState<PromoItem[]>([]);
  const [externalUrl, setExternalUrl] = useState('');
  const [coupons, setCoupons] = useState<Array<{ code: string; description?: string }>>([]);
  const [isAffiliated, setIsAffiliated] = useState(false);
  const [isFreebee, setIsFreebee] = useState(false);
  const [startAt, setStartAt] = useState('');
  const [expiryAt, setExpiryAt] = useState('');
  const [isPromosCategory, setIsPromosCategory] = useState(false);

  // Detectar si la categoría seleccionada es "Promos"
  useEffect(() => {
    const checkIfPromos = async () => {
      if (!categoryId) {
        setIsPromosCategory(false);
        return;
      }
      try {
        const categories = await getAllCategories();
        const promosCategory = categories.find(c => c.name === 'Promos');
        setIsPromosCategory(categoryId === promosCategory?.id);
      } catch (e) {
        console.error('Error fetching categories:', e);
        setIsPromosCategory(false);
      }
    };
    checkIfPromos();
  }, [categoryId]);

  // Auto-seleccionar categoría Promos cuando proposedContentType es 'promo'
  useEffect(() => {
    if (proposedContentType === 'promo') {
      const selectPromosCategory = async () => {
        try {
          const categories = await getAllCategories();
          const promosCategory = categories.find(c => c.name === 'Promos');
          if (promosCategory && categoryId !== promosCategory.id) {
            setCategoryId(promosCategory.id);
          }
        } catch (e) {
          console.error('Error auto-selecting Promos category:', e);
        }
      };
      selectPromosCategory();
    }
  }, [proposedContentType]);

  // Cargar borrador al montar o establecer ubicación por defecto
  useEffect(() => {
    let hasDraftLocation = false;
    try {
      const saved = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (saved) {
        const draft = JSON.parse(saved);
        if (draft.title) setTitle(draft.title);
        if (draft.content) setContent(draft.content);
        if (draft.categoryId) setCategoryId(draft.categoryId);
        if (draft.images && Array.isArray(draft.images) && draft.images.length > 0) {
          setImages(draft.images);
          setMainImage(draft.mainImage || draft.images[0] || null);
        }
        if (draft.location) {
          setLocation(draft.location);
          hasDraftLocation = true;
          defaultLocationSetRef.current = true;
        }
      }
    } catch (e) {
      console.error('Error loading draft', e);
    }

    // Si no hay borrador con ubicación y aún no hemos puesto una por defecto
    if (!hasDraftLocation && relevantLocation && !defaultLocationSetRef.current) {
      setLocation(relevantLocation);
      defaultLocationSetRef.current = true;
    }
  }, [relevantLocation]);

  // Guardar borrador al cambiar campos clave
  useEffect(() => {
    if (loading) return;
    const timer = setTimeout(() => {
      // No guardar si todo está vacío
      const plainText = getPlainTextFromHtml(content);
      const hasContent = title?.trim() || plainText.length > 0;
      if (hasContent || categoryId || location || images.length > 0) {
        const draftToSave = {
          title: title || '', 
          content: content || '', 
          categoryId, 
          location,
          images,
          mainImage
        };
        localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draftToSave));
      } else {
        // Si todo está vacío, no guardar draft
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [title, content, categoryId, location, images, mainImage, loading]);

  // Callback memoizado para cambios de imágenes
  const handleImagesChange = useCallback((allImageUrls: string[], mainImageUrl: string | null, deleted: string[]) => {
    setImages(allImageUrls);
    setMainImage(mainImageUrl);
    setImagesToDelete(deleted);
  }, []);

  const handleSubmit = useCallback(async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    
    // Validar que los campos no estén vacíos
    const titleTrimmed = (title || '').trim();
    const contentTrimmed = (content || '').trim();
    const plainText = getPlainTextFromHtml(contentTrimmed);
    
    
    // Validar título
    if (!titleTrimmed || titleTrimmed.length === 0) {
      toast({ title: 'Error', description: 'El título no puede estar vacío.', variant: 'destructive' });
      return;
    }
    
    // Validar contenido
    if (!plainText || plainText.length === 0) {
      toast({ title: 'Error', description: 'El contenido no puede estar vacío.', variant: 'destructive' });
      return;
    }
    
    
    setLoading(true);
    const payload = {
      title,
      content,
      categoryId,
      location,
      photoUrl: mainImage,
      imageUrls: images,
      userId: session?.user?.id,
      // DEALS/PROMOS fields
      storeName,
      vendorLocation,
      promoItems,
      externalUrl,
      coupons,
      isAffiliated,
      isFreebee,
      startAt,
      expiryAt,
    };
    try {
      const note = await createNote(
        payload.userId,
        payload.title,
        payload.content,
        payload.photoUrl,
        payload.imageUrls,
        payload.location?.displayName || null,
        payload.location?.latitude || null,
        payload.location?.longitude || null,
        payload.categoryId,
        // DEALS/PROMOS parameters
        payload.storeName || null,
        payload.vendorLocation?.latitude || null,
        payload.vendorLocation?.longitude || null,
        payload.promoItems && payload.promoItems.length > 0 ? payload.promoItems : null,
        payload.externalUrl || null,
        payload.coupons && payload.coupons.length > 0 ? payload.coupons : null,
        payload.isAffiliated,
        payload.isFreebee,
        payload.startAt || null,
        payload.expiryAt || null
      );
      
      // Limpiar borrador tras éxito
      localStorage.removeItem(DRAFT_STORAGE_KEY);
      
      // Resetear formulario para nueva nota
      setTitle('');
      setContent('');
      setCategoryId(null);
      setImages([]);
      setMainImage(null);
      setLocation(null);
      // Reset DEALS/PROMOS fields
      setStoreName('');
      setVendorLocation(null);
      setPromoItems([]);
      setExternalUrl('');
      setCoupons([]);
      setIsAffiliated(false);
      setIsFreebee(false);
      setStartAt('');
      setExpiryAt('');
      
      toast({ title: 'Nota creada', description: 'Tu nota fue publicada.' });
      
      // Notificar a otros componentes que hay contenido nuevo
      window.dispatchEvent(new CustomEvent('newsfeed-refresh'));
      
      if (onSuccess) onSuccess(note?.id);
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo crear la nota.' });
    } finally {
      setLoading(false);
    }
  }, [title, content, categoryId, location, mainImage, images, session, toast, onSuccess, storeName, vendorLocation, promoItems, externalUrl, coupons, isAffiliated, isFreebee, startAt, expiryAt]);

  // Expose submit method to parent component
  useImperativeHandle(ref, () => ({
    submit: handleSubmit,
    isLoading: loading
  }), [handleSubmit, loading]);

  const formContent = (
    <div className="space-y-4">
      {proposedContentType === 'promo' ? (
        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <div>
            <Label className="text-base font-bold text-primary">Nombre de la Oferta</Label>
            <Input 
              value={title} 
              onChange={e => setTitle(e.target.value)} 
              required 
              maxLength={120} 
              placeholder="Ej: Galaxy S24 Ultra - 30% OFF en Tienda X"
              className="bg-background text-lg py-6 border-primary/20"
              autoFocus
            />
          </div>

          <PromoDetailsPanel
            storeName={storeName}
            onStoreNameChange={setStoreName}
            vendorLocation={vendorLocation}
            onVendorLocationChange={setVendorLocation}
            items={promoItems}
            onItemsChange={setPromoItems}
            externalUrl={externalUrl}
            onExternalUrlChange={setExternalUrl}
            coupons={coupons}
            onCouponsChange={setCoupons}
            isAffiliated={isAffiliated}
            onIsAffiliatedChange={setIsAffiliated}
            isFreebee={isFreebee}
            onIsFreebeeChange={setIsFreebee}
            startAt={startAt}
            onStartAtChange={setStartAt}
            expiryAt={expiryAt}
            onExpiryAtChange={setExpiryAt}
            disabled={loading}
          />

          <div>
            <Label className="text-sm font-medium">Descripción de la Promo</Label>
            <RichTextEditor 
              content={content} 
              onChange={setContent} 
              placeholder="Explicá cómo conseguirla, requisitos, etc..."
            />
          </div>
        </div>
      ) : (
        <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
          <div>
            <Label className="text-base font-semibold">¿Qué está pasando?</Label>
            <Input 
              value={title} 
              onChange={e => setTitle(e.target.value)} 
              required 
              maxLength={120} 
              placeholder="Un título breve y claro"
              className="bg-background text-lg py-6"
              autoFocus
            />
          </div>
          <div>
            <Label className="text-sm font-medium">Cuerpo</Label>
            <RichTextEditor 
              content={content} 
              onChange={setContent} 
              placeholder="Compartí detalles, opiniones o noticias con la comunidad..."
            />
          </div>
          <div>
            <Label className="text-sm font-medium">Categoría</Label>
            <CategorySelector selectedCategoryId={categoryId} onCategoryChange={setCategoryId} disabled={loading} />
          </div>
        </div>
      )}
      
      {/* El resto (Ubicación e Imágenes) se mantiene igual para ambos */}
      <div className="grid gap-2 p-3 bg-muted rounded-md border border-border">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <Label className="text-sm font-medium">Ubicación</Label>
            {!showLocationEditor ? (
              <button
                type="button"
                className={cn(
                  "w-full text-left text-sm mt-1 px-2 py-1 border border-transparent rounded-md hover:bg-muted/50 transition-colors",
                  location?.displayName ? "text-foreground font-medium" : "text-muted-foreground"
                )}
                onClick={() => {
                  setEditingLocation(null);
                  setShowLocationEditor(true);
                  setLocationEditorAutoFocus(true);
                }}
              >
                {(() => {
                  if (location?.displayName) {
                    return truncateBeforeComma(location.displayName) + ' ';
                  }
                  return 'Establecer ubicación para esta nota';
                })()}
              </button>
            ) : null}
          </div>
          <div className="ml-2">
            {!showLocationEditor ? (
              <Button size="sm" variant="ghost" onClick={() => { setShowLocationEditor(true); setLocationEditorAutoFocus(true); setEditingLocation(null); }}>
                Editar
              </Button>
            ) : (
              <div className="flex space-x-2">
                <Button size="sm" variant="ghost" onClick={() => { setShowLocationEditor(false); setLocationEditorAutoFocus(false); }}>
                  Cerrar
                </Button>
              </div>
            )}
          </div>
        </div>
        {showLocationEditor && (
          <div className="mt-2 flex items-start space-x-2">
            <div className="flex-1">
              <LocationInput
                autoFocus={locationEditorAutoFocus}
                forceBlank={locationEditorAutoFocus}
                showLabel={false}
                value={editingLocation || location}
                onChange={(loc) => {
                  setEditingLocation(loc);
                  setLocationEditorAutoFocus(false);
                }}
                disabled={loading}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Button
                size="sm"
                onClick={() => {
                  setLocation(editingLocation);
                  setShowLocationEditor(false);
                  setLocationEditorAutoFocus(false);
                }}
                disabled={loading}
              >
                Guardar
              </Button>
            </div>
          </div>
        )}
      </div>
      <div>
        <Label>Galería de imágenes</Label>
        <ProductImageManager
          session={session}
          initialImageUrls={images}
          initialMainImageUrl={mainImage}
          onImagesChange={handleImagesChange}
          disabled={loading}
        />
      </div>
      {!hideButton && (
        <Button type="submit" disabled={loading} className="w-full">
          {loading ? 'Publicando...' : 'Publicar nota'}
        </Button>
      )}
    </div>
  );

  return (
    <>
      {hideButton ? (
        /* When hideButton is true, just render content (will be wrapped by parent form) */
        formContent
      ) : (
        /* When standalone, wrap in a form */
        <form onSubmit={handleSubmit}>
          {hideCard ? (
            formContent
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Redactar nota</CardTitle>
              </CardHeader>
              <CardContent>
                {formContent}
              </CardContent>
            </Card>
          )}
        </form>
      )}
    </>
  );
});

NoteForm.displayName = 'NoteForm';

export default NoteForm;
